import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import { API_CONFIG, AUTH_TOKEN_KEY } from "../constants";

/**
 * Interface for API error responses
 *
 * NOTE: We have two fields for error messages, message and error.
 * message is the standard field, error is an alternative field that
 * is used by the Laravel backend.
 *
 * TODO: Homogenize the error message field names.
 * @interface ApiErrorResponse
 * @property {string} [message] - Optional error message from the server
 * @property {string} [error] - Optional error message from the server (alternative field)
 */
interface ApiErrorResponse {
  message?: string;
  error?: string;
  [key: string]: any;
}

/**
 * Custom error class for API-related errors
 * @class ApiError
 * @extends Error
 */
export class ApiError extends Error {
  constructor(message: string, public status?: number, public code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/**
 * Base API client for making HTTP requests
 * Handles common HTTP methods and error handling
 */
class ApiClient {
  private client: AxiosInstance;

  constructor(baseURL: string) {
    this.client = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  /**
   * Gets the current authorization headers
   * @private
   * @returns Authorization headers if token is valid
   *
   * NOTE: This only returns a valid authentication header on development
   * environments. In production environments we directly pass the authentication
   * token via HttpOnly cookies.
   */
  private getAuthHeaders(): { Authorization: string } | {} {
    // If not in development environment, return an empty object
    if (process.env.REACT_APP_STAGING_ENVIRONMENT !== "dev") return {};

    // Get the JWT token from localStorage
    const jwtToken = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!jwtToken) {
      console.error("No JWT token found in localStorage.");
      return {};
    }

    // Return the Authorization header with the JWT token
    console.info("Retrieved JWT token from local storage.");
    return { Authorization: `Bearer ${jwtToken}` };
  }

  /**
   * Handles API errors and converts them to ApiError instances
   * @private
   * @throws {ApiError} Always throws an ApiError
   */
  private handleError(error: unknown): never {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (!axiosError.response) {
        throw new ApiError(
          "Network error - please check your internet connection",
          undefined,
          "NETWORK_ERROR"
        );
      }
      throw new ApiError(
        axiosError.response.data?.message ||
          axiosError.response.data?.error ||
          "API request failed",
        axiosError.response.status,
        axiosError.code
      );
    }
    throw new ApiError("An unexpected error occurred");
  }

  /**
   * Makes a generic HTTP request
   * @template T - Response data type
   * @throws {ApiError} If the request fails
   *
   * NOTE: We set withCredentials to true to send the HttpOnly cookie to the backend.
   * This will authenticate the user on the backend, if the route needs authentication.
   */
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const authHeaders = this.getAuthHeaders();
      const response = await this.client.request<T>({
        ...config,
        headers: {
          ...config.headers,
          ...authHeaders,
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Makes a GET request
   * @template T - Response data type
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "GET", url });
  }

  /**
   * Makes a POST request
   * @template T - Response data type
   */
  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return this.request<T>({ ...config, method: "POST", url, data });
  }

  /**
   * Makes a streaming POST request using fetch
   * Used for chat messages that need to stream responses
   * @param {string} url - The URL to send the request to
   * @param {any} [data] - Optional data to send with the request
   * @param {AbortSignal} [signal] - Optional AbortSignal to abort the request
   * @returns {Promise<Response>} The fetch response object
   * @throws {ApiError} If the request fails
   */
  async postStream(
    url: string,
    data?: any,
    signal?: AbortSignal
  ): Promise<Response> {
    try {
      const authHeaders = this.getAuthHeaders();
      const response = await fetch(`${this.client.defaults.baseURL}${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(data),
        credentials: "include",
        signal,
      });

      if (!response.ok) {
        const errorData = (await response
          .json()
          .catch(() => ({}))) as ApiErrorResponse;
        throw new ApiError(
          errorData.message || errorData.error || "Failed to send message",
          response.status,
          `HTTP_${response.status}`
        );
      }

      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new ApiError(error.message, undefined, "FETCH_ERROR");
      }
      throw new ApiError("Failed to send message");
    }
  }

  /**
   * Makes a PUT request
   * @template T - Response data type
   */
  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return this.request<T>({ ...config, method: "PUT", url, data });
  }

  /**
   * Makes a DELETE request
   * @template T - Response data type
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "DELETE", url });
  }
}

// Create specific API client instances
export const materiomaiApiClient = new ApiClient(
  API_CONFIG.MATERIOM_AI_BACKEND_URL
);

export const commonsApiClient = new ApiClient(API_CONFIG.COMMONS_BACKEND_URL);
